/**
 * KAPE 2.0 — Preview Lesson as Student button
 *
 * Renders a compact button that either opens a modal iframe of the
 * student preview view OR launches the preview in a new tab. Requests
 * a signed short-lived token from `/kcis/admin/kape/courses/{id}/preview-token`.
 */
import { useState } from "react";
import { toast } from "sonner";
import { X, ExternalLink, Eye } from "lucide-react";
import { api } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

export default function PreviewAsStudentButton({
  courseId, lessonId = "", label = "Preview as student",
  variant = "primary", size = "sm", disabled = false,
  className = "", testIdSuffix = "",
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");

  const requestToken = async () => {
    try {
      setBusy(true);
      const { data } = await api.post(
        `/kcis/admin/kape/courses/${courseId}/preview-token`,
        { lesson_id: lessonId, ttl_seconds: 1800 }
      );
      const url = `${BACKEND}/kape/preview?token=${encodeURIComponent(data.token)}`;
      setPreviewUrl(url);
      return url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open preview");
      throw e;
    } finally {
      setBusy(false);
    }
  };

  const openModal = async () => {
    try {
      await requestToken();
      setOpen(true);
    } catch {/* toast handled */}
  };

  const openNewTab = async () => {
    try {
      const url = await requestToken();
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {/* toast handled */}
  };

  const styles = {
    primary: "bg-purple-900 text-white hover:bg-purple-800",
    ghost:   "border border-purple-300 text-purple-900 hover:bg-purple-50",
  }[variant] || "";
  const sizes = { sm: "text-xs px-3 py-1", md: "text-sm px-4 py-2" }[size] || "text-xs px-3 py-1";

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        disabled={disabled || busy || !courseId}
        className={`inline-flex items-center gap-1.5 rounded-full font-semibold disabled:opacity-40 ${styles} ${sizes} ${className}`}
        data-testid={`preview-as-student-btn${testIdSuffix ? `-${testIdSuffix}` : ""}`}
      >
        <Eye className="w-3.5 h-3.5" />
        {busy ? "Opening…" : label}
      </button>

      {open && previewUrl && (
        <div
          className="fixed inset-0 z-[80] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          data-testid="preview-as-student-modal"
          onKeyDown={(e) => { if (e.key === "Escape") setOpen(false); }}
        >
          <div className="w-full max-w-6xl h-[90vh] rounded-2xl overflow-hidden bg-white shadow-2xl flex flex-col">
            <div className="flex items-center gap-3 px-4 py-2 border-b border-slate-200 bg-slate-50">
              <span className="text-xs uppercase tracking-widest font-semibold text-purple-800">Faculty preview</span>
              <span className="text-xs text-slate-500 truncate">student LMS view · signed token</span>
              <a
                href={previewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-auto text-xs text-purple-800 hover:underline inline-flex items-center gap-1"
                data-testid="preview-as-student-new-tab"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                Open in new tab
              </a>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-full p-1 hover:bg-slate-200"
                aria-label="Close preview"
                data-testid="preview-as-student-close"
              >
                <X className="w-4 h-4 text-slate-600" />
              </button>
            </div>
            <iframe
              src={previewUrl}
              className="flex-1 w-full bg-white"
              title="Preview lesson as student"
              data-testid="preview-as-student-iframe"
            />
          </div>
        </div>
      )}
    </>
  );
}
